import {JobUpsertResult, UpsertableShinwaJob, upsertShinwaJobVerb,} from "../../../rpc/webrpc-client.gen.ts";
import {createFileRoute} from "@tanstack/react-router";
import {BulkUploader} from "../../../upload/bulk-upload.tsx";
import {useTranslation} from "react-i18next";
import {ShinwaJobCapsule} from "../../../job/capsule.tsx";
import {isEmpty} from "lodash";
import {CompanyCapsule} from "../../../company/capsule.tsx";
import {VenueCapsule} from "../../../venue/capsule.tsx";
import {TruckCapsule} from "../../../truck/capsule.tsx";
import {TruckModelCapsule} from "../../../truck-model/capsule.tsx";
import {AgentCapsule} from "../../../agent/capsule.tsx";
import {Box, Sheet, Stack} from "@mui/joy";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {useMothership} from "../../../tq/tqx.tsx";

const shinwaJobCsvHeaderTransformMap: Record<string, string> = {
    "timestamp": "timestamp",
    "受注番号": "orderId",
    "受入番号": "intakeId",
    "運転手名": "assignedDriverName",
    "運転手CD": "assignedDriverCode",
    "車種名": "assignedTruckModelName",
    "車種CD": "assignedTruckModelCode",
    "運搬業者名": "transportCompanyName",
    "運搬業者CD": "transportCompanyCode",
    "車番": "assignedTruckPlate",
    "車番CD": "assignedTruckCode",
    "現場着時間日付": "pickupDate",
    "現場着時間（From）": "pickupStartTime",
    "現場着時間（To)": "pickupEndTime",
    "処分事業者名": "dropoffCompanyName",
    "処分事業者名　（カナ）": "dropoffCompanyKana",
    "処分事業者CD": "dropoffCompanyCode",
    "処分事業者住所（郵便番号）": "dropoffCompanyAddressPostCode",
    "処分事業者住所（都道府県）": "dropoffCompanyAddress0",
    "処分事業者住所1": "dropoffCompanyAddress1",
    "処分事業者住所2": "dropoffCompanyAddress2",
    "処分事業者TEL": "dropoffCompanyTel",
    "処分業種区分": "dropoffCompanyKind",
    "処分者行政区分ID": "dropoffCompanyKindCode",
    "荷卸予定処分場名": "dropoffVenueName",
    "処分現場名（カナ）": "dropoffVenueKana",
    "処分場現場CD": "dropoffVenueCode",
    "処分現場住所（郵便番号）": "dropoffVenueAddressPostCode",
    "処分現場住所（都道府県）": "dropoffVenueAddress0",
    "処分現場住所1": "dropoffVenueAddress3",
    "処分現場住所2": "dropoffVenueAddress4",
    "処分現場TEL": "dropoffVenueTel",
    "処分jwnet加入者番号": "dropoffJwnetId",
    "処分edi加入者番号": "dropoffEdiId",
    "処分現場業種区分": "dropoffVenueKind",
    "処分行政区分ID": "dropoffVenueKindCode",
    "排出事業者名": "pickupCompanyName",
    "排出事業者名　（カナ）": "pickupCompanyKana",
    "排出事業者CD": "pickupCompanyCode",
    "排出事業者住所（郵便番号）": "pickupCompanyAddressCode",
    "排出事業者住所（都道府県）": "pickupCompanyAddress0",
    "排出事業者住所1": "pickupCompanyAddress3",
    "排出事業者住所2": "pickupCompanyAddress4",
    "排出事業者TEL": "pickupCompanyTel",
    "排出業種区分": "pickupCompanyKind",
    "排出行政区分ID": "pickupCompanyKindCode",
    "取引先名称": "transferName",
    "取引先CD": "transferCode",
    "排出現場名": "pickupVenueName",
    "排出現場名（カナ）": "pickupVenueKana",
    "排出現場CD": "pickupVenueCode",
    "排出現場住所（郵便番号）": "pickupVenueAddress1",
    "排出現場住所（都道府県）": "pickupVenueAddress0",
    "排出現場住所1": "pickupVenueAddress3",
    "排出現場住所2": "pickupVenueAddress4",
    "排出現場TEL": "pickupVenueTel",
    "排出現場jwnet加入者番号": "pickupJwnetId",
    "排出現場EDI接続番号": "pickupEdiId",
    "排出現場業種区分": "pickupVenueKind",
    "排出現場行政区分ID": "pickupVenueKindCode",
    "排出現場備考": "pickupInstructions",
    "規制内容": "regulatoryInfo",
    "オーダー担当": "contactName",
    "受注備考２": "orderNotes1",
    "受注備考３": "orderNotes2",
    "事務所住所": "officeAddress",
    "事務所TEL": "officeTel",
    "事務所ADDL": "additionalContacts",
    "電話番号": "contactTel",
    "マニフェスト種類": "manifestType",
    "取引区分": "transferKind",
    "作業区分": "businessKind",
    "付帯作業": "additionalWork",
    "遠方割増": "extraDistance",
    "要写真": "keyPhoto",
    "要台貫": "keyBoard",
    "事前協議交付番号": "prepNumber",
    "契約処分場": "contractDropoffSite",
    "経路情報": "pathInfo",
    "特記事項": "specialNotes",
    "運転手指示事項１": "driverNotes1",
    "運転手指示事項２": "driverNotes2",
    "運転手指示事項３": "driverNotes3",
    "運転手指示事項４": "driverNotes4",
    "運転手指示事項５": "driverNotes5",
    "配車URL（メール本文記載分）": "detailUrl",
    "現場営業担当（メール本文記載分）": "salesContactName",
    "現場営業担当ＣＤ": "salesContactCode",
    "巡回分": "comboTripSlot",
    "特定車種": "specialTruck",
    "ドッキング": "docking",
};

function UploadShinwaJob() {
    const {t} = useTranslation();
    const mama = useMothership();
    return <PageShell>
        <PageHeader><TopBar/></PageHeader>
        <PageBody>
            <BulkUploader<UpsertableShinwaJob, UpsertableShinwaJob, JobUpsertResult>
                entityTypeLabel={t("entity.job")}
                headerTransformMap={shinwaJobCsvHeaderTransformMap}
                csvToUpsertFood={(csv) => csv}
                upsertTqo={{
                    mutationKey: [upsertShinwaJobVerb],
                    mutationFn: (args) => mama.upsertShinwaJob(args)
                        .then(tqrv => tqrv.result),
                }}
                upsertFoodCapsule={(food) => {
                    const displayName = food.orderId ?? ("X" + food.intakeId!);
                    return <Sheet color="primary" variant="soft">
                        <ShinwaJobCapsule job={{displayName}}/>
                    </Sheet>
                }}
                existingId={(result) => result.existingId ?? null}
                newId={(result) => result.newId ?? null}
                completedCapsule={({food, color, variant}) => {
                    const displayName = food.orderId ?? ("X" + food.intakeId!);
                    return <Sheet color={color} variant={variant}>
                        <ShinwaJobCapsule job={{displayName}}/>
                    </Sheet>;
                }}
                conflictCapsules={(tqr) => {
                    const conflicts: React.JSX.Element[] = [];
                    if (tqr.conflict) {
                        conflicts.push(<ShinwaJobCapsule key="job" color="danger" job={tqr.conflict}/>);
                    }
                    if (tqr.companyConflict) {
                        conflicts.push(<Stack direction="row" sx={{alignItems: "center"}}>
                            <Box>{t("conflict.requested")}</Box>
                            <CompanyCapsule key="company" color="danger" company={tqr.companyConflict.requested}/>;
                            <Box>{t("conflict.existing")}</Box>
                            <CompanyCapsule key="company" color="danger" company={tqr.companyConflict.existing}/>
                        </Stack>);
                    }
                    if (tqr.venueConflict) {
                        conflicts.push(<Stack direction="row" sx={{alignItems: "center"}}>
                            <Box>{t("conflict.requested")}</Box>
                            <VenueCapsule key="venue" color="danger" venue={tqr.venueConflict.requested}/>
                            <Box>{t("conflict.existing")}</Box>
                            <VenueCapsule key="venue" color="danger" venue={tqr.venueConflict.existing}/>
                        </Stack>);
                    }
                    if (tqr.truckConflict) {
                        conflicts.push(<Stack direction="row" sx={{alignItems: "center"}}>
                            <Box>{t("conflict.requested")}</Box>
                            <TruckCapsule key="truck" color="danger" truck={tqr.truckConflict.requested}/>
                            <Box>{t("conflict.existing")}</Box>
                            <TruckCapsule key="truck" color="danger" truck={tqr.truckConflict.existing}/>
                        </Stack>);
                    }
                    if (tqr.truckModelConflict) {
                        conflicts.push(<TruckModelCapsule key="truckModel" color="danger"
                                                          entity={tqr.truckModelConflict}/>);
                    }
                    if (tqr.driverConflict) {
                        conflicts.push(<AgentCapsule key="agent" color="danger" agent={tqr.driverConflict}/>);
                    }
                    return isEmpty(conflicts) ? null : conflicts;
                }}/>
        </PageBody>
    </PageShell>


}

export function isShinwaJobInternal(maybe: Partial<UpsertableShinwaJob>): boolean {
    return !!maybe.orderId;
}

export const Route = createFileRoute("/_auth/upload/shinwa-job")({
    component: UploadShinwaJob,
});

